<template>
    <div>
        <batch-report-filter ref="batchReportFilter"
                             :all-report-types="allReportTypes"
                             :all-report-options="allReportOptions"
                             :all-work-item-type-options="allWorkItemTypeOptions"
                             :all-transcriber-options="allTranscriberOptions"
                             :all-work-item-status-options="allWorkItemStatusOptions"
                             :all-order-delivery-status-options="allOrderDeliveryStatusOptions"
                             :all-court-registry-options="allCourtRegistryOptions"
                             :all-court-jurisdiction-options="allCourtJurisdictionOptions"
                             :all-court-location-options="allCourtLocationOptions"
                             :all-interview-organisation-options="allInterviewOrganisationOptions"
                             :all-ordering-client-options="allOrderingClientOptions"
                             :all-service-type-options="allServiceTypeOptions"
                             :all-service-delay-options="allServiceDelayOptions"
                             :all-order-status-options="allOrderStatusOptions"
                             :all-user-account-type-options="allUserAccountTypeOptions"
                             :all-user-status-options="allUserStatusOptions"
                             :all-user-role-options="allUserRoleOptions"
                             :all-agvsa-clearance-options="allAgvsaClearanceOptions"
                             :all-typist-skill-level-options="allTypistSkillLevelOptions"
                             :all-employment-type-options="allEmploymentTypeOptions"
                             :court-related-work-item-options="courtRelatedWorkItemOptions"
                             :interview-related-work-item-options="interviewRelatedWorkItemOptions"
                             :all-invoice-reference-options="allInvoiceReferenceOptions"
                             :all-qc-options="allQcOptions"
                             :allow-public="allowPublic"
                             v-on:filterCreated="refreshGrid">
        </batch-report-filter>

        <filter-ui :api-base-url="apiBaseUrl" :fields="filterFields" relatedTo="batchReport" :filter="initialGridFilter" v-on:change="gridFilterChanged"></filter-ui>
        <ejs-grid id="batchReportsGrid"
                  ref="batchReportsGrid"
                  :dataSource="gridData"
                  :allowSorting="true"
                  :sortSettings="sortSettings"
                  :toolbar="toolbar"
                  :allowGrouping="false"
                  :groupSettings="groupSettings"
                  :allowResizing="true"
                  :allowPaging="true"
                  :pageSettings="pageSettings"
                  :showColumnChooser="true"
                  :allowExcelExport="false"
                  :dataStateChange="gridDataStateChanged"
                  :rowSelected="rowSelectionChanged"
                  :rowDeselected="rowSelectionChanged"
                  :toolbarClick="toolbarClicked"
                  :created="gridCreated"
                  :queryCellInfo="customiseCell"
                  :headerCellInfo="headerCellInfoEvent"
                  :enableStickyHeader="true">
            <e-columns>
                <e-column field="requestedDateTimeUTC" headerText="Requested" width="100"></e-column>
                <e-column field="name" headerText="Name"></e-column>
                <e-column field="reportTypeDisplayText" headerText="Report Type"></e-column>
                <e-column field="requestedByUserDisplayName" headerText="Requested By"></e-column>
                <e-column field="statusDisplayText" headerText="Status"></e-column>
                <e-column field="processedDateTimeUTC" headerText="Processed"></e-column>
                <e-column field="processingErrorMessage" headerText="Processing Message"></e-column>
                <e-column field="isPublicDisplayText" headerText="Public"></e-column>
            </e-columns>
        </ejs-grid>

        <div id="includePublicToolbarSwitch">
            <b-form-checkbox switch v-model="gridDataState.includePublic" @change="refreshGrid">
                Include Public
            </b-form-checkbox>
        </div>
    </div>

</template>

<script>
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import { GridPlugin, ColumnChooser, Resize, Sort, Group, Page, Toolbar, Search, DetailRow } from '@syncfusion/ej2-vue-grids';
    import axios from 'axios';
    import Visibility from 'visibilityjs';

    import FilterUI from './FilterUI';

    import BatchReportFilter from './BatchReportFilter';

    import ErrorsMixin from '../../mixins/ErrorsMixin';
    import DateTimeMixin from '../../mixins/DateTimeMixin';
    import GridMixins from '../../mixins/GridMixins';

    Vue.use(BootstrapVue);
    Vue.use(GridPlugin);

    window.Visibility = Visibility;
    //this is required to make the getGroupCaption function available globally and thus within the groupCaptionTemplate
    var batchReportsGrid_vueComponent = null;
    function batchReportsGrid_getGroupCaption(field, headerText, value) {
        return batchReportsGrid_vueComponent.getGroupCaption(field, headerText, value);
    }
    window.batchReportsGrid_getGroupCaption = batchReportsGrid_getGroupCaption;

    export default {
        name: 'BatchReportGrid',
        mixins: [ErrorsMixin, DateTimeMixin, GridMixins],
        components: {
            'batch-report-filter': BatchReportFilter, 'filter-ui': FilterUI
        },
        provide: {
            grid: [ColumnChooser, Resize, Sort, Group, Page, Toolbar, Search, DetailRow]
        },
        props: {
            apiBaseUrl: {
                type: String,
                required: true
            },
            fileDownloadUrl: {
                type: String,
                default: null
            },
            pagesBaseUrl: {
                type: String,
                required: true
            },
            canViewBatchReports: {
                type: Boolean,
                default: false
            },
            allReportTypes: {
                type: Array,
                default: null
            },
            allReportOptions: {
                type: Array,
                default: null
            },
            allWorkItemTypeOptions: {
                type: Array,
                default: null
            },
            allTranscriberOptions: {
                type: Array,
                default: null
            },
            allWorkItemStatusOptions: {
                type: Array,
                default: null
            },
            allOrderDeliveryStatusOptions: {
                type: Array,
                default: null
            },
            allCourtRegistryOptions: {
                type: Array,
                default: null
            },
            allCourtJurisdictionOptions: {
                type: Array,
                default: null
            },
            allCourtLocationOptions: {
                type: Array,
                default: null
            },
            allInterviewOrganisationOptions: {
                type: Array,
                default: null
            },
            allOrderingClientOptions: {
                type: Array,
                default: null
            },
            allServiceTypeOptions: {
                type: Array,
                default: null
            },
            allServiceDelayOptions: {
                type: Array,
                default: null
            },
            allOrderStatusOptions: {
                type: Array,
                default: null
            },
            courtRelatedWorkItemOptions: {
                type: Array,
                default: null
            },
            allInvoiceReferenceOptions: {
                type: Array,
                default: null
            },
            interviewRelatedWorkItemOptions: {
                type: Array,
                default: null
            },
            allUserAccountTypeOptions: {
                type: Array,
                default: null
            },
            allUserStatusOptions: {
                type: Array,
                default: null
            },
            allUserRoleOptions: {
                type: Array,
                default: null
            },
            allAgvsaClearanceOptions: {
                type: Array,
                default: null
            },
            allTypistSkillLevelOptions: {
                type: Array,
                default: null
            },
            allEmploymentTypeOptions: {
                type: Array,
                default: null
            },
            currentUserId: {
                type: String,
                default: null
            },
            initialInterviewRelatedWorkItemId: {
                type: String,
                default: null
            },
            allowPublic: {
                type: Boolean,
                default: true
            },
            allQcOptions: {
                type: Array,
                required: true
            },
        },
        data: function () {

            const now = new Date()
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
            const minDate = new Date(today)
            // set date picker min date to '2 months ago'
            minDate.setMonth(minDate.getMonth() - 2)
            // set date picker max date to today
            const maxDate = new Date(today)

            return {
                groupSettings: { showGroupedColumn: true, disablePageWiseAggregates: true, captionTemplate: "${batchReportsGrid_getGroupCaption(field, headerText, key)}" },
                pageSettings: { pageSize: 10, pageSizes: [5, 10, 25, 50] },
                min: minDate,
                max: maxDate,
                batchReportRequests: null,
                confirmDelete: false,
                batchReportNameFilter: '',
                includePublic: false,
                startDate: null,
                endDate: null,
                
                
                refreshNeeded: false,

                initialGridFilter: null,

                showErrors: false,
                currentErrors: null,
                disableReason: null,
                timer: null,
                initialized: false,
                filterFields: [
                    {
                        field: "requestedDateTimeUTC",
                        label: "Requested Date",
                        type: "date",
                        format: "dd/MM/yyyy"
                    },
                    {
                        field: "reportType",
                        label: "Report Type",
                        type: "multiselect",
                        options: this.allReportTypes,
                        placeholder: "Select Report Type"
                    }
                ],
                sortSettings: { columns: [{ field: "requestedDateTimeUTC", direction: "Descending" }] },
                columnFormats: {
                    'requestedDateTimeUTC': {
                        type: 'datetime'
                    },
                    'processedDateTimeUTC': {
                        type: 'datetime'
                    },
                },
                gridDataState: {
                    includePublic: true,
                    filter: null,
                    search: null,
                    sorted: [{ name: "requestedDateTimeUTC", direction: "descending" }],
                    group: null,
                    skip: 0,
                    take: 10,
                    requiresCounts: true
                },
                gridData: { result: [], count: 0 },
                grid: null,

            }
        },
        watch: {
            refreshNeeded: function () {
                if (this.refreshNeeded) {
                    this.timer = Visibility.every(30000, this.refreshGrid);
                }
                else {
                    Visibility.stop(this.timer);
                    this.timer = null;
                }
            }
        },
        computed: {

            toolbar: function () {
                var toolbarItems = this.defaultEJ2DataGridToolbarItems({ grouping: false, export: false, columnchooser: true });

                toolbarItems =
                    toolbarItems.concat(
                        [
                            { text: "Download", tooltipText: "Download batch report", prefixIcon: "fas fa-file-alt", id: "download" },
                            { text: "View Options", tooltipText: "View batch report options", prefixIcon: "fas fa-file-alt", id: "view" },
                            { text: "Copy", tooltipText: "Copy batch report", prefixIcon: "fas fa-file-alt", id: "copy" },
                            { text: "Delete", tooltipText: "Delete batch report request", prefixIcon: "fas fa-times", id: "delete" }
                        ]
                    );
                toolbarItems = [{ template: "#includePublicToolbarSwitch", align: 'Right' }].concat(toolbarItems);
                return toolbarItems;
            }
        },
        methods: {

            refreshGrid: function () {
                this.grid.showSpinner();
                this.loadBatchReportRequests(this.gridDataState)
                    .then(result => {
                        if (result) {
                            this.gridData = result;
                        }
                        this.grid.hideSpinner();
                    });

            },
            gridCreated: function () {
                Vue.nextTick(() => {
                    //this.defaultEJ2DataGridCreatedHandler(this.grid);
                });
            },
            gridFilterChanged: function (filter) {
                this.gridDataState.filter = filter;
                if (this.initialized) this.grid.refresh();
            },
            getGroupCaption: function (field, headerText, value) {
                if (field == "requestedDateTimeUTC") value = this.dateTimeDisplayString(value, 'includetime', true, false);
                else if (field == "processedDateTimeUTC") value = this.dateTimeDisplayString(value, 'includetime', true, false);
                return headerText + ": " + value;
            },
            customizeGroupKeys(groupedDate) {
                groupedDate.forEach(g => {
                    if (g.field && g.key && g.items) {
                        if (g.field == "requestedDateTimeUTC") g.key = this.dateTimeDisplayString(g.key, 'includetime', true, false);
                        else if (g.field == "processedDateTimeUTC") g.key = this.dateTimeDisplayString(g.key, 'includetime', true, false);
                        this.customizeGroupKeys(g.items);
                    }
                });
            },
            customiseCell: function (args) {
                this.defaultEJ2DataGridQueryCellInfo(args, this.columnFormats);
            },

            gridDataStateChanged: function (args) {
                this.gridDataState.search = args.search;
                this.gridDataState.sorted = args.sorted;
                this.gridDataState.group = args.group;
                this.gridDataState.skip = args.skip;
                this.gridDataState.take = args.take;
                this.gridDataState.requiresCounts = args.requiresCounts;
                if (this.initialized) {
                    this.refreshGrid();
                }
            },
            toolbarClicked: function (args) {
                if ((args.item.id === 'view') ||
                    (args.item.id === 'delete') ||
                    (args.item.id === 'copy') ||
                    (args.item.id === 'download')) {
                    this.handleAction(args.item.id, this.grid.getSelectedRecords()[0]);
                } else {
                    this.defaultEJ2DataGridToolbarClickHandler(args, this.grid);
                }
            },
            handleAction: function (action, batchReportRequest) {

                if (action === 'delete') {
                    this.deleteBatchReportRequest(batchReportRequest);
                }
                else if (action === 'view') {
                    this.viewFilter(batchReportRequest, true);
                }
                else if (action === 'copy') {
                    this.viewFilter(batchReportRequest, false);
                }
                else if (action === 'download') {
                    var url = this.documentDownloadUrl(batchReportRequest);
                    window.open(url);
                }
            },
            rowSelectionChanged: function (args) {
                if (args.name == 'rowSelected') {
                    this.updateToolbarItems(this.grid.getSelectedRecords()[0])
                } else {
                    this.updateToolbarItems(null)
                }
            },
            updateToolbarItems: function (item = null) {
                this.setToolbarItemState('download', (item != null) && (item.batchReportRequestFileId != null));
                this.setToolbarItemState('delete', (item != null) && (this.belongsToCurrentUser(item)));
                this.setToolbarItemState('copy', (item != null));
                this.setToolbarItemState('view', (item != null));
            },
            setToolbarItemState: function (itemId, enabled) {
                this.grid.toolbarModule.enableItems([itemId], enabled);
            },

            loadBatchReportRequests: function (gridDataState) {

                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += 'report/batchreportrequestlist';
                return axios
                    .post(url, JSON.stringify(gridDataState), {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    .then(response => {
                        var gridData = response.data;
                        this.refreshNeeded = gridData.result.some((result) => (result.ProcessedDateTimeUTC == null) && (result.disabledDateTimeUTC == null));
                        if (gridData && gridData.result) {
                            gridData.result = gridData.result.map((batchReportRequest) => {
                                if (batchReportRequest.requestedDateTimeUTC) batchReportRequest.requestedDateTimeUTC = this.parseDateTime(batchReportRequest.requestedDateTimeUTC, true, false);
                                if (batchReportRequest.processedDateTimeUTC) batchReportRequest.processedDateTimeUTC = this.parseDateTime(batchReportRequest.processedDateTimeUTC, true, false);
                                return batchReportRequest;
                            });
                        }
                        return gridData;
                    })
                    .catch(ex => {
                        var errors = this.handleAxiosException(ex);
                        this.showErrorsToast(errors);
                        return false;
                    });
            },           
            deleteBatchReportRequest: function (batchReportRequest) {
                this.confirmDelete = false;
                this.$bvModal.msgBoxConfirm('Delete report?', {
                    title: 'Please Confirm',
                    size: 'sm',
                    buttonSize: 'sm',
                    okVariant: 'danger',
                    okTitle: 'YES',
                    cancelTitle: 'NO',
                    footerClass: 'p-2',
                    hideHeaderClose: false,
                    centered: true
                })
                    .then(value => {
                        this.confirmDelete = value;
                        if (this.confirmDelete) {
                            this.handleDeleteBatchReportRequest(batchReportRequest.batchReportRequestId);
                        }
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                    });
            },
            handleDeleteBatchReportRequest: function (batchReportRequestId) {
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += 'report/' + batchReportRequestId;
                axios
                    .delete(url)
                    .then(() => {
                        this.refreshGrid();
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                    });
            },
            getEmptyText: function (emptyText) {
                emptyText = "No records to display ";
                return emptyText;
            },
            documentDownloadUrl: function (batchReportRequestDocument) {
                if (!this.fileDownloadUrl)
                    return null;
                if (this.fileDownloadUrl.indexOf('00000000-0000-0000-0000-000000000000') >= 0)
                    return this.fileDownloadUrl.replace('00000000-0000-0000-0000-000000000000', batchReportRequestDocument.batchReportRequestFileId)
                var url = this.fileDownloadUrl;
                if (!url.endsWith('/')) url += '/';
                url += batchReportRequestDocument.batchReportRequestFileId;
                return url;
            },
            belongsToCurrentUser: function (batchReportRequest) {
                return (batchReportRequest.requestedByUserAccountId == this.currentUserId);
            },
            viewFilter: function (batchReportRequestFilter, readOnly) {
                var filterString = JSON.stringify(batchReportRequestFilter.filter);
                var filter = JSON.parse(filterString);
                this.$refs.batchReportFilter.viewOrCopyReportRequest(
                    readOnly ? batchReportRequestFilter.name : null,
                    batchReportRequestFilter.reportTypeString,
                    filter,
                    batchReportRequestFilter.columns,
                    batchReportRequestFilter.sorted,
                    batchReportRequestFilter.isPublic,
                    readOnly);
            },
            documentDownloadAvailable: function (batchReportRequest) {
                return (batchReportRequest.batchReportRequestFileId);
            }
        },
        mounted: function () {
            batchReportsGrid_vueComponent = this;
            this.grid = this.$refs.batchReportsGrid.ej2Instances;
            this.setupGridPersistence(this.grid);
            this.updateToolbarItems(this.grid, null);
            this.initialized = true;
            this.refreshGrid();
        }
    }
</script>

<style scoped>
    #includePublicToolbarSwitch >>> label {
        font-size: 14px;
        padding-top: 0.1rem;
    }
</style>